import React, { useEffect, useState } from 'react';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { Save, Loader2, User } from 'lucide-react';

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="text-xs font-bold uppercase tracking-wider text-[#1A2A4F]/70">{label}</span>
    <input
      {...props}
      className="mt-1 w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#F5A623]/30 focus:border-[#F5A623] outline-none disabled:bg-gray-50"
    />
  </label>
);

const MyProfilePage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    const load = async () => {
      try {
        const r = await api().get('/student/me/profile');
        setForm(r.data || {});
      } catch { toast.error('Could not load profile'); }
      setLoading(false);
    };
    load();
  }, [api]);

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      await api().put('/student/me/profile', {
        name: form.name, phone: form.phone, country: form.country,
        nationality: form.nationality, date_of_birth: form.date_of_birth,
        passport_number: form.passport_number, study_level: form.study_level,
        preferred_country: form.preferred_country, preferred_intake: form.preferred_intake,
        bio: form.bio,
      });
      toast.success('Profile saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  if (loading) {
    return <p className="text-center py-10 text-gray-500" data-testid="profile-loading">Loading…</p>;
  }

  return (
    <form onSubmit={save} className="space-y-6" data-testid="my-profile-page">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-[#F5A623]/15 text-[#F5A623] flex items-center justify-center">
          <User className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-[#1A2A4F]">Your Profile</h1>
          <p className="text-sm text-gray-600">Keep your details up to date so we can match you to programs faster.</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Full Name" value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name" />
        <Field label="Email" value={form.email || ''} disabled data-testid="profile-email" />
        <Field label="Phone" value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="profile-phone" />
        <Field label="Date of Birth" type="date" value={form.date_of_birth || ''} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} data-testid="profile-dob" />
        <Field label="Nationality" value={form.nationality || ''} onChange={(e) => setForm({ ...form, nationality: e.target.value })} data-testid="profile-nationality" />
        <Field label="Country of Residence" value={form.country || ''} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="profile-country" />
        <Field label="Passport Number" value={form.passport_number || ''} onChange={(e) => setForm({ ...form, passport_number: e.target.value })} data-testid="profile-passport" />
        <Field label="Study Level (e.g. Master's)" value={form.study_level || ''} onChange={(e) => setForm({ ...form, study_level: e.target.value })} data-testid="profile-study-level" />
        <Field label="Preferred Country to Study" value={form.preferred_country || ''} onChange={(e) => setForm({ ...form, preferred_country: e.target.value })} data-testid="profile-preferred-country" />
        <Field label="Preferred Intake" value={form.preferred_intake || ''} onChange={(e) => setForm({ ...form, preferred_intake: e.target.value })} data-testid="profile-preferred-intake" />
        <label className="sm:col-span-2 block">
          <span className="text-xs font-bold uppercase tracking-wider text-[#1A2A4F]/70">Bio / About you</span>
          <textarea
            rows={3}
            value={form.bio || ''}
            onChange={(e) => setForm({ ...form, bio: e.target.value })}
            placeholder="Tell us about your goals, work experience, achievements…"
            className="mt-1 w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#F5A623]/30 focus:border-[#F5A623] outline-none"
            data-testid="profile-bio"
          />
        </label>
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center gap-2 bg-[#F5A623] text-white font-bold px-6 py-2.5 rounded-md hover:brightness-95 disabled:opacity-50"
          data-testid="save-profile-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save profile
        </button>
      </div>
    </form>
  );
};

export default MyProfilePage;
